/**
 * Limites e controles de funcionalidades para o modo BETA.
 *
 * `isBetaMode` é uma variável de módulo para funções utilitárias puras (sem React).
 * Em componentes React, usar o hook `useBetaMode()` do AppContext para reatividade.
 * O AppProvider (AppContext.tsx) chama `initBetaMode()` na montagem da aplicação.
 */

// eslint-disable-next-line prefer-const
export let isBetaMode = false

/**
 * Inicializa o modo BETA. Chamado pelo AppProvider na montagem.
 * Em componentes React, preferir `useBetaMode()` hook para reatividade.
 */
export function initBetaMode(betaModeEnabled: boolean): void {
  isBetaMode = betaModeEnabled
}

/** Constantes de limite BETA — mutáveis para permitir atualização via API */
// eslint-disable-next-line prefer-const
export let BETA_LIMITS = {
  /** Tamanho máximo por arquivo (MB) — atualizado via initBetaLimits() */
  fileSizeMB: 200,
  /** Máximo de arquivos por envio — atualizado via initBetaLimits() */
  maxConcurrentUploads: 2,
  /** Cota diária de uploads — controlada pelo backend via getUploadQuota() */
  dailyUploadQuota: 5,
  /** Máximo de regenerações de resumo por arquivo — atualizado via initBetaLimits() */
  maxSummaryRegenerations: 3,
  /** Comparação de transcrições habilitada — atualizada via initBetaLimits() */
  comparisonEnabled: false,
}

/**
 * Atualiza os limites BETA com os valores resolvidos pelo backend a partir das
 * tags FluentCRM do usuário. Chamado pelo AppProvider logo após receber getUploadQuota().
 *
 * @param limits Limites retornados pelo endpoint GET /api/v1/quota
 */
export function initBetaLimits(limits: {
  maxFileSizeBytes: number
  maxConcurrentUploads: number
  maxSummaryRegenerations: number
  comparisonEnabled: boolean
}): void {
  BETA_LIMITS.fileSizeMB = Math.floor(limits.maxFileSizeBytes / (1024 * 1024))
  BETA_LIMITS.maxConcurrentUploads = limits.maxConcurrentUploads
  BETA_LIMITS.maxSummaryRegenerations = limits.maxSummaryRegenerations
  BETA_LIMITS.comparisonEnabled = limits.comparisonEnabled
}

// ─── Regenerações de resumo ───────────────────────────────────────────────────

function regenKey(fileId: string): string {
  return `beta_regen_count_${fileId}`
}

/** Retorna quantas vezes o resumo deste arquivo já foi regenerado */
export function getSummaryRegenCount(fileId: string): number {
  if (!isBetaMode) return 0
  return parseInt(localStorage.getItem(regenKey(fileId)) ?? '0', 10)
}

/** Retorna true se ainda é possível regenerar o resumo deste arquivo */
export function canRegenerateSummary(fileId: string): boolean {
  if (!isBetaMode) return true
  return getSummaryRegenCount(fileId) < BETA_LIMITS.maxSummaryRegenerations
}

/** Incrementa o contador de regeneração para o arquivo */
export function incrementSummaryRegenCount(fileId: string): void {
  if (!isBetaMode) return
  const current = getSummaryRegenCount(fileId)
  localStorage.setItem(regenKey(fileId), String(current + 1))
}

// ─── Helpers de verificação ───────────────────────────────────────────────────

/**
 * Retorna true se comparação de archivos está habilitada.
 * Quando não está em beta, sempre permitida.
 * Quando em beta, respeita a constante BETA_LIMITS.comparisonEnabled.
 */
export function isComparisonEnabled(): boolean {
  if (!isBetaMode) return true
  return BETA_LIMITS.comparisonEnabled
}

/**
 * Retorna true se é possível iniciar upload da quantidade de arquivos indicada,
 * dado o número de uploads restantes retornado pelo backend.
 * Quando não está em beta, sempre permitido.
 *
 * @param count     Número de arquivos que o usuário deseja enviar
 * @param remaining Uploads restantes hoje (vindo do backend via getUploadQuota)
 */
export function canUploadFiles(count: number, remaining: number): boolean {
  if (!isBetaMode) return true
  return remaining >= count
}
